import DataTable from "../../components/common/DataTable";
import React from "react";
import { Progress } from "@mantine/core";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { notifications } from "@mantine/notifications";
import { AnalyticsPageLayout, StatCard, ChartCard, TOOLTIP_STYLE, chartProps } from "./AnalyticsLayout";
import { Users } from "lucide-react";

const userDemographics = [
  { key: "1", ageGroup: "13-18", users: 125400, activeToday: 89320, avgSessionTime: "42 mins", retention: 72, premiumUsers: 18810 },
  { key: "2", ageGroup: "19-25", users: 250800, activeToday: 175560, avgSessionTime: "45 mins", retention: 78, premiumUsers: 75240 },
  { key: "3", ageGroup: "26-35", users: 380200, activeToday: 228120, avgSessionTime: "48 mins", retention: 82, premiumUsers: 114060 },
  { key: "4", ageGroup: "36-45", users: 210500, activeToday: 105250, avgSessionTime: "38 mins", retention: 75, premiumUsers: 63150 },
  { key: "5", ageGroup: "45+", users: 98300, activeToday: 39320, avgSessionTime: "35 mins", retention: 68, premiumUsers: 19660 },
];

const userColumns = [
  { title: "Age Group", dataIndex: "ageGroup", key: "ageGroup" },
  { title: "Total Users", dataIndex: "users", key: "users", render: (t) => t.toLocaleString() },
  { title: "Active Today", dataIndex: "activeToday", key: "activeToday", render: (t) => t.toLocaleString() },
  { title: "Avg Session", dataIndex: "avgSessionTime", key: "avgSessionTime" },
  { title: "Retention", dataIndex: "retention", key: "retention", render: (t) => <Progress value={t} color="green" size="sm" style={{ width: 60 }} /> },
  { title: "Premium Users", dataIndex: "premiumUsers", key: "premiumUsers", render: (t) => t.toLocaleString() },
];

const userRegions = [
  { name: "North America", value: 35, color: "#3b82f6" },
  { name: "Europe", value: 28, color: "#10b981" },
  { name: "Asia Pacific", value: 25, color: "#f59e0b" },
  { name: "Latin America", value: 8, color: "#8b5cf6" },
  { name: "Middle East", value: 4, color: "#06b6d4" },
];

const userGrowthTrend = [
  { month: "Jan", newUsers: 25000, activeUsers: 520000, churnRate: 3.2 },
  { month: "Feb", newUsers: 28000, activeUsers: 548000, churnRate: 3.0 },
  { month: "Mar", newUsers: 32000, activeUsers: 580000, churnRate: 2.8 },
  { month: "Apr", newUsers: 35000, activeUsers: 615000, churnRate: 2.5 },
];

const userEngagementTrend = [
  { week: "Week 1", avgSessionLength: 42, avgSessions: 3.2, watchedContent: 1.5 },
  { week: "Week 2", avgSessionLength: 44, avgSessions: 3.4, watchedContent: 1.6 },
  { week: "Week 3", avgSessionLength: 46, avgSessions: 3.6, watchedContent: 1.7 },
  { week: "Week 4", avgSessionLength: 48, avgSessions: 3.8, watchedContent: 1.8 },
];

const UserAnalyticsPage = () => {
  const handleExport = (format) => {
    notifications.show({ message: `Exporting as ${format.toUpperCase()}...`, color: "green" });
  };

  return (
    <AnalyticsPageLayout
      icon={Users}
      title="User Analytics"
      subtitle="User demographics, engagement & retention metrics"
      onExport={handleExport}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total Users" value={1065200} suffix="" />
        <StatCard title="Active Users (Today)" value={637570} suffix="" />
        <StatCard title="Retention Rate" value={75.2} suffix="%" />
        <StatCard title="Premium Users" value={290920} suffix="" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="User Growth & Churn Trend">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userGrowthTrend}>
                <CartesianGrid {...chartProps.grid} />
                <XAxis dataKey="month" {...chartProps.axis} />
                <YAxis {...chartProps.axis} tickFormatter={(v) => v >= 1000 ? `${(v / 1000)}k` : v} />
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="newUsers" stroke="#3b82f6" strokeWidth={2} name="New Users" />
                <Line type="monotone" dataKey="activeUsers" stroke="#10b981" strokeWidth={2} name="Active Users" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
        <ChartCard title="Geographic Distribution">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={userRegions} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name} ${value}%`} labelLine={false}>
                  {userRegions.map((e, i) => <Cell key={i} fill={e.color} stroke="#0a0a0a" strokeWidth={2} />)}
                </Pie>
                <RechartsTooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n, p) => [`${v}%`, p.payload.name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="mb-8">
      <ChartCard title="User Engagement Metrics">
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={userEngagementTrend}>
              <CartesianGrid {...chartProps.grid} />
              <XAxis dataKey="week" {...chartProps.axis} />
              <YAxis {...chartProps.axis} />
              <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="avgSessionLength" fill="#3b82f6" name="Avg Session (min)" />
              <Bar dataKey="avgSessions" fill="#10b981" name="Avg Sessions/Day" />
              <Bar dataKey="watchedContent" fill="#f59e0b" name="Avg Content/Day (hrs)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
      </div>

      <ChartCard title="User Demographics by Age Group">
        <DataTable dataSource={userDemographics} columns={userColumns} pagination={false} scroll={{ x: 1000 }} rowKey="key" />
      </ChartCard>
    </AnalyticsPageLayout>
  );
};

export default UserAnalyticsPage;
